import React, { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import { Grid2 } from '@mui/material'
import { Field } from 'formik'
import { Button } from '@mui/material'
import * as Yup from 'yup'
import { TextFormField } from '../../../shared/components/formfields/TextFormField'
import { Grid_Gap } from '../../../shared/constants'
import { useToaster } from '../../../shared/utils/ToasterProvider'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useContinentCodeList } from '../../../assets/api/useAdminApi'
import { AutoCompleteField } from '../../../shared/components/formfields/AutoCompleteField'
import api from '../../../assets/nodeApi/axiosInstance'
import CustomDivider from '../../../shared/utils/CustomDivider'

const CountryForm = ({ initialCountry = null, isEditing = false, onSuccess = () => { } }) => {
    const { data: continentCodeList, isLoading: continentLoading } = useContinentCodeList();
    const { showToaster } = useToaster();
    const queryClient = useQueryClient();

    const continentOptions = continentCodeList?.data?.map((item, index) => ({
        id: index + 1,
        label: item?.continent_code,
        value: item?.continent_code
    })) || [];
    const initialValues = {
        country_name: initialCountry?.country_name || '',
        continent_code: initialCountry?.continent_code || '',
    }

    const validationSchema = Yup.object().shape({
        country_name: Yup.string().required('Country name is required'),
        continent_code: Yup.string().required('Continent code is required'),
    })

    const createCountryMutation = useMutation({
        mutationFn: (formData) => api.post("country/create", formData),
    });

    const updateCountryMutation = useMutation({
        mutationFn: (formData) => api.put(`country-update/${initialCountry?.id}`, formData),
    });

    const handleSubmit = (values, { resetForm, setSubmitting }) => {
        if (isEditing) {
            updateCountryMutation.mutate(values, {
                onSuccess: (response) => {
                    if (response?.data?.message) {
                        showToaster("Country updated successfully!", '', 'success');
                        queryClient.invalidateQueries({ queryKey: ["countries-all-list"] });
                        onSuccess();
                    } else {
                        showToaster('Something went wrong!', '', 'error');
                    }
                },
                onError: (error) => {
                    showToaster(error?.data?.error || "Failed to update country", '', 'error');
                }
            });
        } else {
            createCountryMutation.mutate(values, {
                onSuccess: (response) => {
                    if (response?.data?.message) {
                        showToaster("Country added successfully!", '', 'success')
                        resetForm();
                        queryClient.invalidateQueries({ queryKey: ["countries-all-list"] });
                    } else {
                        showToaster('Something went wrong!', '', 'error')
                    }
                },
                onError: (error) => {
                    showToaster(error?.data?.error || "Failed to add country", '', 'error')
                }
            });
        }
        resetForm();
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
            {({ resetForm }) => (
                <Form>
                    <Grid2 container spacing={"20px"}>
                        <Grid2 children size={4}>
                            <Field
                                name="country_name"
                                component={TextFormField}
                                placeholder="Enter Country Name"
                                label="Country Name"
                                isRequired={true}
                            />
                        </Grid2>
                        <Grid2 children size={4}>
                            <Field
                                name="continent_code"
                                component={AutoCompleteField}
                                placeholder="Select Continent Code"
                                options={continentOptions}
                                label="Continent Code"
                                isRequired={true}
                                isLoading={continentLoading}
                            />
                        </Grid2>
                    </Grid2>
                    <CustomDivider />
                    <Grid2 container spacing={Grid_Gap} sx={{ display: "flex", justifyContent: "end" }}>
                        <Grid2 children size={{ xs: 12, sm: 3 }}
                            onClick={() => {
                                resetForm();
                                if (isEditing) {
                                    onSuccess();
                                }
                            }}
                        >
                            <Button variant='outlined' fullWidth onClick={resetForm}>
                                {isEditing ? "Cancel" : "Reset"}
                            </Button>
                        </Grid2>
                        <Grid2 children size={{ xs: 12, sm: 3 }}
                            disabled={createCountryMutation.isLoading || updateCountryMutation.isLoading}
                        >
                            <Button variant='contained' type="submit" fullWidth>
                                {isEditing ? "Update" : "Submit"}
                            </Button>
                        </Grid2>
                    </Grid2>
                </Form>
            )}
        </Formik>
    )
}

export default CountryForm